import { readButtonConfig } from '@/lib/blocks/button-links';
import { pickStringUnion, readImageValue, readString, readVideoValue } from '@/lib/prop-utils';
import { BLOCK_TYPE, type PageBlock } from '@/types/block';
import { ButtonBlock } from '../primitives/button-block';
import { EmbedBlock } from '../primitives/embed-block';
import {
  FORM_BLOCK_BACKGROUND_VALUES,
  FORM_BLOCK_HEADING_ALIGNMENT_VALUES,
  FormBlock,
  type FormBlockProps,
} from '../primitives/form-block';
import { IMAGE_BLOCK_SHAPE_VALUES, ImageBlock } from '../primitives/image-block';
import { PracticeAreaTagsBlock } from '../primitives/practice-area-tags-block';
import { RawHtmlBlock } from '../primitives/raw-html-block';
import { ReviewIndexBlock } from '../primitives/review-index-block';
import { TextBlock } from '../primitives/text-block';
import { VIDEO_BLOCK_ASPECT_VALUES, VideoBlock } from '../primitives/video-block';

function assertUnreachable(value: never): never {
  throw new Error(`Unhandled block type: ${String(value)}`);
}

export function BlockRenderer({
  block,
  interactive = true,
}: {
  block: PageBlock;
  interactive?: boolean;
}) {
  const props = block.props || {};

  switch (block.blockType) {
    case 'text':
      if (readString(props.renderStyle) === 'tags') {
        return <PracticeAreaTagsBlock content={props.content} />;
      }
      return <TextBlock content={props.content} />;
    case 'image':
      return (
        <ImageBlock
          image={readImageValue(props.image)}
          blockProps={props}
          shape={pickStringUnion(readString(props.shape), IMAGE_BLOCK_SHAPE_VALUES, 'rounded')}
        />
      );
    case 'video':
      return (
        <VideoBlock
          video={readVideoValue(props.video)}
          controls={props.controls !== false}
          autoplay={!!props.autoplay}
          loop={!!props.loop}
          muted={!!props.muted}
          aspect={pickStringUnion(readString(props.aspect), VIDEO_BLOCK_ASPECT_VALUES, 'wide')}
        />
      );
    case 'button':
      return <ButtonBlock {...readButtonConfig(block.props)} />;
    case 'embed':
      return (
        <EmbedBlock
          html={readString(props.html)}
          resolvedHtml={readString(props.resolvedHtml)}
          url={readString(props.url)}
          interactive={interactive}
        />
      );
    case 'raw_html':
      return (
        <RawHtmlBlock
          html={readString(props.resolvedHtml) ?? readString(props.html)}
          interactive={interactive}
        />
      );
    case 'form':
      return (
        <FormBlock
          formId={readString(props.formId)}
          form={props.form as FormBlockProps['form']}
          submitLabel={readString(props.submitLabel)}
          background={pickStringUnion(
            readString(props.background),
            FORM_BLOCK_BACKGROUND_VALUES,
            'white',
          )}
          headingAlignment={pickStringUnion(
            readString(props.headingAlignment),
            FORM_BLOCK_HEADING_ALIGNMENT_VALUES,
            'left',
          )}
        />
      );
    case BLOCK_TYPE.REVIEW_INDEX:
      return <ReviewIndexBlock props={props} />;
    case 'group':
    case 'collection':
    case 'blog_widget':
    case 'case_result_widget':
    case 'index_collection':
    case 'review_widget':
      return null;
    default:
      return assertUnreachable(block.blockType);
  }
}
